import shpwrite from "@mapbox/shp-write";

/**
 * Create random KML color
 * @param {number} index - sub polygon index
 * @returns {string} - KML color format (aabbggrr)
 */
const generateColor = (index) => {
  const colors = [
    "7f0000ff",
    "7f00ff00",
    "7fff0000",
    "7f00ffff",
    "7fff00ff",
    "7fffff00",
  ];

  if (index >= colors.length) {
    const r = Math.floor(Math.random() * 255)
      .toString(16)
      .padStart(2, "0");
    const g = Math.floor(Math.random() * 255)
      .toString(16)
      .padStart(2, "0");
    const b = Math.floor(Math.random() * 255)
      .toString(16)
      .padStart(2, "0");
    return `7f${b}${g}${r}`;
  }

  return colors[index];
};

/**
 * Create the KML content from coordinates
 * @param {Object} field - Object with field's data
 * @returns {string} - string with KML document
 */

export const generateKML = (field) => {
  const generateStyles = (fieldParts) => {
    const mainStyle = `
      <Style id="mainFieldStyle">
        <PolyStyle>
          <color>7f0000ff</color>
          <outline>1</outline>
        </PolyStyle>
        <LineStyle>
          <color>ff000000</color>
          <width>2</width>
        </LineStyle>
      </Style>
    `;

    const subStyles =
      fieldParts
        ?.map(
          (_, index) => `
      <Style id="subField${index}Style">
        <PolyStyle>
          <color>${generateColor(index)}</color>
          <outline>1</outline>
        </PolyStyle>
        <LineStyle>
          <color>ff000000</color>
          <width>1</width>
        </LineStyle>
      </Style>
    `
        )
        .join("\n") || "";

    return mainStyle + subStyles;
  };

  const generatePlacemark = (
    polygonData,
    isMainField = false,
    styleId = "mainFieldStyle"
  ) => {
    const coordinates = polygonData?.polygon
      ?.map((coord) => `${coord[1]},${coord[0]}, 0`)
      .join(" ");

    return `
       <Placemark>
       <styleUrl>#${styleId}</styleUrl>
        <name>${polygonData?.name || "Campo"}</name>
        <description>
          <![CDATA[
            Tipo terreno: ${polygonData?.field_type?.name || "N/D"}<br/>
            ${
              isMainField
                ? `Sistema irrigazione: ${
                    polygonData?.irrigation_system?.name || "N/D"
                  }<br/>`
                : ""
            }
            Percentuale organica: ${
              polygonData?.organic_soil_percentage || 0
            }%<br/>
            Percentuale pietrosa: ${polygonData?.stone_percentage || 0}%
          ]]>
        </description>
        <Polygon>
          <outerBoundaryIs>
            <LinearRing>
              <coordinates>${coordinates}</coordinates>
            </LinearRing>
          </outerBoundaryIs>
        </Polygon>
      </Placemark>
    `;
  };

  const mainPlacemark = generatePlacemark(field, true, "mainFieldStyle");

  const subPlacemarks =
    field?.field_part
      ?.map((part, index) =>
        generatePlacemark(part, false, `subField${index}Style`)
      )
      .join("\n") || "";

  const kmlContent = `<?xml version="1.0" encoding="UTF-8"?>
    <kml xmlns="http://www.opengis.net/kml/2.2">
      <Document>
        <name>${field?.name || "Campo"}</name>
        ${generateStyles(field?.field_part)}
        ${mainPlacemark}
        ${subPlacemarks}
      </Document>
    </kml>`;

  return kmlContent;
};

/**
 * Genera il contenuto GeoJSON dalle coordinate fornite
 * @param {Object} field - Oggetto contenente i dati del campo
 * @returns {Object} - Oggetto GeoJSON FeatureCollection
 */
export const generateGeoJSON = (field) => {
  const features = [];

  const closePolygon = (coords) => {
    const first = coords[0];
    const last = coords[coords.length - 1];
    const areEqual = first[0] === last[0] && first[1] === last[1];

    return areEqual ? coords : [...coords, first];
  };

  const buildCoordinates = (polygon) => [
    closePolygon(polygon.map((coord) => [coord[1], coord[0], 0])),
  ];

  const baseProperties = {
    description: null,
    timestamp: null,
    begin: null,
    end: null,
    altitudeMo: null,
    tessellate: -1,
    extrude: 0,
    visibility: -1,
    drawOrder: null,
    icon: null,
  };

  const mainFeature = {
    type: "Feature",
    properties: {
      ...baseProperties,
      Name: field.name || "Campo",
      type: "main_field",
      field_type: field.field_type?.name || "N/D",
      irrigation_system: field.irrigation_system?.name || "N/D",
      organic_soil_percentage: field.organic_soil_percentage || 0,
      stone_percentage: field.stone_percentage || 0,
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [buildCoordinates(field.polygon)],
    },
  };

  features.push(mainFeature);

  if (field.field_parts && Array.isArray(field.field_parts)) {
    field.field_parts.forEach((part, index) => {
      const subFeature = {
        type: "Feature",
        properties: {
          ...baseProperties,
          Name: part.name || `Sottopoigono ${index + 1}`,
          type: "sub_field",
          parent_field: field.name || "Campo",
          field_type: part.field_type?.name || "N/D",
          organic_soil_percentage: part.organic_soil_percentage || 0,
          stone_percentage: part.stone_percentage || 0,
        },
        geometry: {
          type: "MultiPolygon",
          coordinates: [buildCoordinates(field.polygon)],
        },
      };
      features.push(subFeature);
    });
  }

  const geoJson = {
    type: "FeatureCollection",
    features: features,
  };

  return geoJson;
};

/**
 * Funzione generica per scaricare un file
 * @param {string|Object} content - Contenuto del file
 * @param {string} filename - Nome del file
 * @param {string} format - Formato del file (kml, geojson, shp)
 */
export const downloadFile = async (content, filename, format) => {
  let blob;
  let fileExtension;
  let mimeType;

  switch (format) {
    case "kml":
      mimeType = "application/vnd.google-earth.kml+xml";
      fileExtension = "kml";
      blob = new Blob([content], { type: mimeType });
      break;
    case "geoJson":
      mimeType = "application/geo+json";
      fileExtension = "geojson";
      blob = new Blob([JSON.stringify(content)], { type: mimeType });
      break;
    case "shp":
      mimeType = "application/zip";
      fileExtension = "zip";
      const zipData = await content;
      blob = new Blob([zipData], { type: mimeType });
      break;
    default:
      console.error("Formato non supportato");
      return;
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.${fileExtension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Create shapefile from field data
 * @param {Object} field - Object with field's data
 * @returns {string} - string with shapefile document
 */

export const generateSHP = async (field) => {
  const features = [];

  const closePolygon = (coords) => {
    const first = coords[0];
    const last = coords[coords.length - 1];
    const areEqual = first[0] === last[0] && first[1] === last[1];

    return areEqual ? coords : [...coords, first];
  };

  const buildCoordinates = (polygon) => [
    closePolygon(polygon.map((coord) => [coord[1], coord[0]])),
  ];

  const baseProperties = {
    descriptio: null,
    timestamp: null,
    begin: null,
    end: null,
    altitudeMo: null,
    tessellate: -1,
    extrude: 0,
    visibility: -1,
    drawOrder: null,
    icon: null,
  };

  const mainFeature = {
    type: "Feature",
    properties: {
      ...baseProperties,
      Name: field?.name || "Campo",
      type: "main_field",
      fieldType: field?.field_type?.name || "N/D",
      irrigation: field?.irrigation_system?.name || "N/D",
      organic: field?.organic_soil_percentage || 0,
      stone: field.stone_percentage || 0,
    },
    geometry: {
      type: "Polygon",
      coordinates: buildCoordinates(field.polygon),
    },
  };
  features.push(mainFeature);

  if (field?.field_parts && Array.isArray(field.field_parts)) {
    field.field_parts.forEach((part, index) => {
      const subFeature = {
        type: "Feature",
        properties: {
          ...baseProperties,
          Name: part.name || `Sottopoligono ${index + 1}`,
          type: "sub_field",
          fieldType: part.field_type?.name || "N/D",
          organic: part.organic_soil_percentage || 0,
          stone: part.stone_percentage || 0,
        },
        geometry: {
          type: "Polygon",
          coordinates: buildCoordinates(field.polygon),
        },
      };
      features.push(subFeature);
    });
  }

  const geojsonData = {
    type: "FeatureCollection",
    features: features,
  };

  try {
    const zipData = await shpwrite.zip(geojsonData);

    const binaryString = window.atob(zipData);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes.buffer;
  } catch (error) {
    console.error("Errore nella generazione dello shapefile:", error);
    throw error;
  }
};
